import { Translations } from '../translations.model';

export const EN: Translations = {
  brand: 'Benji Rodríguez',
  role: 'Software Engineer',
  heroGreeting: "Hi, I'm",
  heroFirstName: 'Benjamin',
  heroLastName: 'Rodriguez',
  heroHeadline: 'Software Engineer specializing in TypeScript',
  heroLead:
    'I build web applications and business systems end to end, working with modern frontend, backend, database, and cloud technologies.',
  viewExperience: 'View experience',
  viewProjects: 'View projects',
  downloadCv: 'Download CV',
  contactMe: 'Contact me',
  connect: "Let's connect:",
  aboutTitle: 'About me',
  aboutParagraphs: [
    "I'm a Software Engineer with experience in frontend and backend development, primarily specialized in TypeScript. I've built and maintained business applications using Angular, React, NestJS, Express, and Node.js, along with databases such as PostgreSQL and MySQL.",
    'I also have hands-on experience with AWS, Docker, Linux, third-party integrations, and production deployments. In addition, I have working knowledge of Java with Spring Boot and Go using Gin and GORM.',
    "Throughout my career I've contributed across the software lifecycle—from implementing requirements and shipping features to integrations, deployments, incident response, and production support.",
    'I want to keep growing as a Software Engineer, going deeper into backend, architecture, cloud, and distributed systems.',
  ],
  aboutStats: [
    { value: '~3', label: 'Years of experience' },
    { value: 'TypeScript', label: 'Core stack' },
    { value: 'Full Stack', label: 'Frontend and backend' },
    { value: 'Panama', label: 'Location', emoji: '🇵🇦' },
  ],
  experienceTitle: 'Experience',
  experiences: [
    {
      company: 'Confidential client',
      title: 'Software Engineer',
      type: 'Part-time',
      period: 'March 2026 — Present',
      location: 'Remote',
      current: true,
      description:
        'Backend and web application development and maintenance for a confidential client, including integrations, infrastructure, and production support.',
      responsibilities: [
        'Development and maintenance with Ruby on Rails.',
        'PostgreSQL work, including SQL query optimization.',
        'JavaScript and TypeScript development.',
        'Third-party service integrations.',
        'Implementing and maintaining business processes.',
        'Building and maintaining reports.',
        'Production deployments.',
        'Infrastructure administration and support.',
        'Production incident response.',
      ],
      technologies: [
        'Ruby on Rails',
        'PostgreSQL',
        'JavaScript',
        'TypeScript',
        'AWS',
        'Linux',
        'Nginx',
        'Puma',
        'Sidekiq',
        'Redis',
      ],
    },
    {
      company: 'SMS Sudamérica',
      title: 'Angular Front-End Developer',
      type: 'Mid-Level',
      period: 'October 2025 — Present',
      location: 'Remote',
      current: true,
      description:
        'Development and maintenance of business applications using Angular and TypeScript.',
      responsibilities: [
        'Building new features.',
        'Creating reusable components.',
        'Integrating REST APIs.',
        'Troubleshooting and resolving issues.',
        'Maintaining and improving existing functionality.',
        'Collaborating with backend and cross-functional teams.',
        'Using Docker in the development workflow.',
      ],
      technologies: ['Angular', 'TypeScript', 'CSS', 'Docker', 'REST APIs'],
    },
    {
      company: 'Etyalab',
      title: 'Full-Stack Developer',
      period: 'May 2024 — October 2025',
      location: 'Panama',
      description:
        'I worked as a Full Stack developer building and maintaining several business applications.',
      responsibilities: [
        'Frontend and backend development.',
        'Implementing new features.',
        'APIs and integrations.',
        'Maintaining existing applications.',
        'Working with PostgreSQL databases.',
        'Deployments and support using AWS and Docker.',
        'Contributing to systems related to payments, dashboards, shift management, and operational processes.',
      ],
      technologies: [
        'React',
        'Angular',
        'Vue',
        'NestJS',
        'LoopBack',
        'Express.js',
        'PostgreSQL',
        'AWS EC2',
        'AWS S3',
        'Docker',
        'Bash',
      ],
    },
    {
      company: 'Maxia',
      title: 'Full-Stack Developer',
      type: 'Intern',
      period: 'February 2023 — June 2023',
      location: 'Panama',
      description:
        'I joined as an intern through a course at Universidad Tecnológica de Panamá and contributed to web development projects as a Full Stack developer, mainly working with React and web backends.',
      responsibilities: [
        'Building web features.',
        'Frontend work with React.',
        'Backend support.',
        'Basic application maintenance.',
        'Applying solid development practices.',
      ],
      technologies: ['React', 'PHP'],
    },
  ],
  referencesTitle: 'Code references',
  referencesLead:
    'These repositories show what I have practiced in backend, frontend, and architecture. They are not deployed; the code lives on GitHub.',
  viewOnGithub: 'View repository',
  viewAllGithub: 'View GitHub',
  references: [
    {
      title: 'EventFlow',
      description:
        'Order flow with a transactional outbox: the order and event are persisted in the same PostgreSQL transaction, a publisher sends them to Kafka, and a consumer records notifications. I practiced consistency, recovery when Kafka is down, Docker Compose, Flyway, and Testcontainers-based tests.',
      technologies: [
        'Java',
        'Quarkus',
        'Kafka',
        'PostgreSQL',
        'React',
        'TypeScript',
        'Docker',
      ],
      repositoryUrl: 'https://github.com/DevBeex/eventflow',
    },
    {
      title: 'Search Engine',
      description:
        'In-memory search engine with Spring Boot: inverted index, concurrent reads with a ReadWriteLock, Spring events for analytics, and an LRU cache. I also packaged the app with a multi-stage Dockerfile.',
      technologies: ['Java', 'Spring Boot', 'Docker'],
      repositoryUrl: 'https://github.com/DevBeex/search-engine',
    },
    {
      title: 'Taskflow',
      description:
        'Board-style backend (Trello-inspired) with Spring Boot, JPA, Spring Security, Flyway, and PostgreSQL. The repo focuses on the API and persistence side of that experiment.',
      technologies: ['Java', 'Spring Boot', 'PostgreSQL', 'Flyway'],
      repositoryUrl: 'https://github.com/DevBeex/taskflow',
    },
    {
      title: 'Music',
      description:
        'Angular app in TypeScript with Tailwind. In this repo I explored a modern frontend structure and AWS Amplify integration.',
      technologies: ['Angular', 'TypeScript', 'Tailwind CSS', 'AWS Amplify'],
      repositoryUrl: 'https://github.com/DevBeex/music',
    },
    {
      title: 'Node.js REST API',
      description:
        'REST API with Express organized into routes, middlewares, schemas, and services. I used it to practice endpoint design, validation, and separation of concerns in Node.js.',
      technologies: ['Node.js', 'Express.js', 'JavaScript'],
      repositoryUrl: 'https://github.com/DevBeex/APIrest-with-nodejs-express',
    },
  ],
  skillsTitle: 'Skills / Stack',
  skillCategories: {
    languages: 'Languages',
    frontend: 'Frontend',
    backend: 'Backend',
    database: 'Databases',
    devops: 'Cloud / DevOps',
    tools: 'Tools',
  },
  educationTitle: 'Education',
  educationDegree: 'Bachelor’s Degree in Software Engineering',
  educationInstitution: 'Universidad Tecnológica de Panamá',
  educationPeriod: '2019 — 2025',
  certificationsTitle: 'Certifications',
  oracleCertificationName: 'Oracle Certified Associate (OCA)',
  oracleCertificationCredential: 'Java SE 8 Programmer',
  oracleCertificationIssuer: 'Oracle',
  oracleCertificationUrl:
    'https://catalog-education.oracle.com/ords/certview/sharebadge?id=B16D2EAD04DF07F5E4E2915729C3FC28917D75ED7DC1365D6BF1269C7099683E',
  coursesTitle: 'Courses',
  platziCourses: [
    {
      name: 'Java Spring',
      url: 'https://platzi.com/p/catsdevops/curso/1996-java-spring/diploma/detalle/',
    },
    {
      name: 'Backend with Node.js',
      url: 'https://platzi.com/p/catsdevops/curso/2485-backend-nodejs/diploma/detalle/',
    },
    {
      name: 'Node.js fundamentals',
      url: 'https://platzi.com/p/catsdevops/curso/1759-fundamentos-node/diploma/detalle/',
    },
    {
      name: 'Practical JavaScript',
      url: 'https://platzi.com/p/catsdevops/curso/3271-course/diploma/detalle/',
    },
  ],
  languagesTitle: 'Languages',
  spokenLanguages: [
    { name: 'Spanish', level: 'Native' },
    { name: 'English', level: 'A2 / In progress' },
  ],
  contactTitle: "Let's talk",
  contactLead:
    "I'm open to new opportunities as a Software Engineer, Full Stack Developer, Frontend Developer, or Backend Developer.",
  currentBadge: 'Current',
  demo: 'Demo',
  code: 'Code',
  navigation: 'Navigation',
  madeWith: 'Made with ♥ and code',
  rights: '© 2026 Benjamin Rodriguez. All rights reserved.',
  navHome: 'Home',
  navAbout: 'About',
  navExperience: 'Experience',
  navReferences: 'References',
  navSkills: 'Skills',
  navEducation: 'Education',
  navContact: 'Contact',
  openMenu: 'Open menu',
  closeMenu: 'Close menu',
};
